import { ChevronLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { SessionSummary, type AppointmentData } from '../../../SessionSummary/SessionSummary';
import { useScheduleFlow } from '../../context/useScheduleFlow';

import { InfoMessage } from '@/components/shared/InfoMessage/InfoMessage';
import { toast } from '@/components/ui/Toast/Toast';
import { useBookAppointment } from '@/features/scheduling/api/bookAppointment';
import { invalidateSchedulingQueries } from '@/features/scheduling/utils/invalidateSchedulingQueries';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useTimeFor } from '@/hooks/useTimeFor';
import { useAuthStore } from '@/stores/authStore';
import type { Id } from '@/types/api';
import { centerDayFromKey, toUtcIsoString } from '@/utils/timezone';
import { endOfDay } from 'date-fns';

interface SummaryStepProps {
  portalContainerRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}

export const SummaryStep = ({ portalContainerRef, onClose }: SummaryStepProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog.steps.summaryStep' });
  const { translateError } = useErrorTranslation();

  const { userData } = useAuthStore();
  const { timeZone: centerTimeZone, formatDate } = useTimeFor('sessionTime');
  const userDataStudents = userData?.students ?? [];

  const { data, goToNextStep, goToPreviousStep, removeStudent, goToStep, setErrorType, updateData } = useScheduleFlow();

  // `recurring.endDate` is a bare center day, so the API and the summary cards both get the last
  // moment of that day in the center's zone.
  const recurrenceEndsAt = data.recurring.endDate ? toUtcIsoString(endOfDay(centerDayFromKey(data.recurring.endDate, centerTimeZone))) : undefined;

  const { mutate: bookAppointment, isPending: isBookingPending } = useBookAppointment({
    options: {
      onSuccess: (data) => {
        void captureAnalyticsEvent('scheduling_complete', { bookedAppointmentsIds: data.bookedAppointments?.map((appt) => appt.appointment_id) ?? [] });
        updateData('bookedAppointmentsDetails', data);
        invalidateSchedulingQueries();
        goToNextStep();
      },
      onError: (error) => {
        if (error.errors.some((e) => e.code === 'TIMESLOT_NOT_AVAILABLE')) {
          setErrorType('fullyBooked');
          goToStep('error');
        } else {
          toast({
            variant: 'destructive',
            description: translateError(error),
          });
        }
      },
    },
  });

  const bookingConflicts = data.bookedAppointmentsDetails?.notPossibleAppointments ?? [];

  const [announcement, setAnnouncement] = useState('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSubmit = () => {
    bookAppointment({
      students: data.studentSessionDetails!.map((student) => ({
        studentId: student.studentId,
        centerId: student.centerDetails.center_id,
        enrollmentId: student.enrollmentId,
        sessionType: student.sessionType,
        deliveryType: student.deliveryType,
        duration: student.durationInMinutes,
      })),
      isPlan: false,
      selectedTimeslot: data.dateAndTime,
      ...(data.recurring.enabled && {
        recurringSettings: {
          recurrencePattern: data.recurring.recurrencePattern!,
          endDate: recurrenceEndsAt!,
        },
      }),
    });
  };

  const handleDeleteStudent = (studentId: Id) => {
    const student = userDataStudents.find((s) => s.id === studentId);

    removeStudent(studentId);

    if (student) {
      setAnnouncement(t('studentRemoved', { studentName: `${student.firstName} ${student.lastName}` }));
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => setAnnouncement(''), 100);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const createAppointmentsForStudents = (): AppointmentData[] => {
    return data.selectedStudentsIds.map((studentId) => {
      const sessionDetail = data.studentSessionDetails?.find((detail) => detail.studentId === studentId);

      if (!sessionDetail) {
        throw new Error(`Session details not found for student ${studentId}`);
      }

      const studentConfig = data.config.data?.students.find((s) => s.id === studentId);
      const enrollment = studentConfig?.enrollments.find((e) => e.id === sessionDetail?.enrollmentId);

      if (!enrollment) {
        throw new Error(`Enrollment not found for student ${studentId}`);
      }

      return {
        recurring_schedule:
          data.recurring.enabled && data.recurring.recurrencePattern && data.recurring.endDate
            ? {
                recurrence_pattern: data.recurring.recurrencePattern,
                effective_until: recurrenceEndsAt!,
              }
            : undefined,
        student_id: studentId,
        appointment_date: data.dateAndTime,
        duration_minutes: sessionDetail?.durationInMinutes,
        enrollment_id: enrollment.id,
      };
    });
  };

  const allAppointments = createAppointmentsForStudents();

  const appointments = allAppointments.filter((appointment) => {
    return !bookingConflicts.some((conflict) => conflict.date === appointment.appointment_date);
  });

  const hasAnyAppointments = appointments.length > 0;

  return (
    <>
      <DialogBody>
        <div className='mb-6'>
          <h3 className='text-heading-m-bold text-text-default-primary mb-2'>{t('title')}</h3>
          <p className='text-body-m-regular text-text-default-secondary'>{t('subheading')}</p>
        </div>

        {bookingConflicts.length > 0 && (
          <InfoMessage variant='warning' title={t('bookingConflict.title')}>
            <p>{t('bookingConflict.description')}</p>
            <ul>
              {bookingConflicts.map((conflict) => (
                <li key={conflict.date} className='ml-5 list-disc'>
                  <time dateTime={conflict.date}>{formatDate(conflict.date)}</time>
                </li>
              ))}
            </ul>
          </InfoMessage>
        )}

        <div className='space-y-4'>
          {appointments.map((appointment, index) => {
            const appointmentStudentConfig = data.config.data?.students.find((studentConfig) => studentConfig.id === appointment.student_id);

            const appointmentEnrollment = appointmentStudentConfig?.enrollments.find((enrollment) => enrollment.id === appointment.enrollment_id);

            return (
              <SessionSummary
                // eslint-disable-next-line react-x/no-array-index-key
                key={`${appointment.student_id}-${index}`}
                appointment={appointment}
                enrollment={appointmentEnrollment}
                onDelete={data.selectedStudentsIds.length > 1 ? () => handleDeleteStudent(appointment.student_id) : undefined}
                portalContainerRef={portalContainerRef}
              />
            );
          })}
        </div>

        <div aria-live='polite' aria-atomic='true' className='sr-only'>
          {announcement}
        </div>
      </DialogBody>

      <DialogFooter>
        <Button type='button' variant='text-secondary' onClick={goToPreviousStep} className='flex-1'>
          <ChevronLeft className='text-text-default-secondary size-5' />
          <span>{t('backButton.label')}</span>
        </Button>
        <Button type='button' onClick={hasAnyAppointments ? handleSubmit : onClose} isLoading={isBookingPending} className='flex-1' disabled={isBookingPending}>
          {t(hasAnyAppointments ? (isBookingPending ? 'confirmButton.loadingLabel' : 'confirmButton.label') : 'cancelButton.label')}
        </Button>
      </DialogFooter>
    </>
  );
};
